import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { useContent } from '../../context/ContentContext';
import { AmbienceItem } from '../../types';

import ambiencePhoto from '../../assets/images/leton_ambience_photo_1790498317790.jpg';
import ambienceCoffeeBar from '../../assets/images/leton_ambience_coffee_bar_1790499800115.jpg';
import ambienceOutdoor from '../../assets/images/leton_ambience_outdoor_seating_1790499815140.jpg';
import ambienceCozy from '../../assets/images/leton_ambience_cozy_corner_1790499829303.jpg';

export const DEFAULT_AMBIENCE_ITEMS: AmbienceItem[] = [
  {
    id: 'default-ambience-1',
    imageUrl: ambiencePhoto,
    alt: 'Atmosfer LetonKu - Ruang Temu Komunitas',
    sortOrder: 1,
    isActive: true,
  },
  {
    id: 'default-ambience-2',
    imageUrl: ambienceCoffeeBar,
    alt: 'Barista Seduh V60 - Specialty Coffee Bar Leton',
    sortOrder: 2,
    isActive: true,
  },
  {
    id: 'default-ambience-3',
    imageUrl: ambienceOutdoor,
    alt: 'Area Outing Taman Terbuka - LetonKu Dusk',
    sortOrder: 3,
    isActive: true,
  },
  {
    id: 'default-ambience-4',
    imageUrl: ambienceCozy,
    alt: 'Sudut Nyaman Cahaya Pagi - LetonKu',
    sortOrder: 4,
    isActive: true,
  },
];

export const AmbienceCarousel: React.FC = () => {
  const { data } = useContent();

  // Determine active carousel items from CMS or fallback to default images
  const activeItems = useMemo(() => {
    if (Array.isArray(data?.ambienceItems) && data.ambienceItems.length > 0) {
      const active = data.ambienceItems
        .filter((item) => item.isActive !== false && item.imageUrl && item.imageUrl.trim().length > 0)
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      if (active.length > 0) return active;
    }
    return DEFAULT_AMBIENCE_ITEMS;
  }, [data?.ambienceItems]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [touchEndX, setTouchEndX] = useState<number | null>(null);

  // Keep index within bounds if activeItems changes
  useEffect(() => {
    if (currentIndex >= activeItems.length) {
      setCurrentIndex(0);
    }
  }, [activeItems.length, currentIndex]);

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % activeItems.length);
  }, [activeItems.length]);

  const handlePrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + activeItems.length) % activeItems.length);
  }, [activeItems.length]);

  // Autoplay timer (5000ms)
  useEffect(() => {
    if (isPaused || activeItems.length <= 1) return;

    const interval = setInterval(() => {
      handleNext();
    }, 5000);

    return () => clearInterval(interval);
  }, [handleNext, isPaused, activeItems.length]);

  // Touch Swipe Handlers for Mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    setIsPaused(true);
    setTouchStartX(e.targetTouches[0].clientX);
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStartX !== null && touchEndX !== null) {
      const distance = touchStartX - touchEndX;
      const minSwipeDistance = 40; // minimum pixels to trigger swipe
      if (distance > minSwipeDistance) {
        // Swiped Left -> Next
        handleNext();
      } else if (distance < -minSwipeDistance) {
        // Swiped Right -> Prev
        handlePrev();
      }
    }
    setTouchStartX(null);
    setTouchEndX(null);
    setIsPaused(false);
  };

  const currentItem = activeItems[currentIndex] || activeItems[0];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.6 }}
      className="pt-10 sm:pt-14 space-y-6 sm:space-y-8"
    >
      {/* Large Horizontal Editorial Photo Carousel Container */}
      <div
        className="w-full relative overflow-hidden rounded-2xl sm:rounded-3xl border border-[#E0F2FE] shadow-xs group bg-[#172033]"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="relative w-full aspect-[16/9] sm:aspect-[21/9] overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.img
              key={currentItem.id || currentIndex}
              src={currentItem.imageUrl || '/assets/leton-coffee-beans.jpg'}
              alt={currentItem.alt || 'Atmosfer LetonKu'}
              loading="lazy"
              decoding="async"
              referrerPolicy="no-referrer"
              onError={(e) => {
                const target = e.currentTarget;
                if (target.src !== '/assets/leton-coffee-beans.jpg' && !target.src.endsWith('/assets/leton-coffee-beans.jpg')) {
                  target.src = '/assets/leton-coffee-beans.jpg';
                }
              }}
              initial={{ opacity: 0.2, scale: 1.03 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0.2 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="w-full h-full object-cover select-none pointer-events-none"
            />
          </AnimatePresence>

          {/* Gradient Overlay for Bottom Indicator Dots Contrast */}
          <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/60 via-black/20 to-transparent pointer-events-none" />

          {/* Desktop Chevron Navigation Arrows */}
          {activeItems.length > 1 && (
            <>
              <button
                type="button"
                onClick={handlePrev}
                aria-label="Foto Sebelumnya"
                className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-black/30 hover:bg-black/60 backdrop-blur-md text-white flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 cursor-pointer shadow-md"
              >
                <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
              <button
                type="button"
                onClick={handleNext}
                aria-label="Foto Selanjutnya"
                className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-black/30 hover:bg-black/60 backdrop-blur-md text-white flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 cursor-pointer shadow-md"
              >
                <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </>
          )}

          {/* Indicator Dots */}
          {activeItems.length > 1 && (
            <div className="absolute bottom-3 sm:bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 rounded-full bg-black/40 backdrop-blur-md">
              {activeItems.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setCurrentIndex(idx)}
                  aria-label={`Lihat foto ${idx + 1}`}
                  className={`transition-all duration-300 rounded-full cursor-pointer ${
                    currentIndex === idx
                      ? 'w-6 h-2 bg-[#38BDF8]'
                      : 'w-2 h-2 bg-white/60 hover:bg-white'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Elegant Editorial Caption Below Carousel */}
      <div className="flex flex-col items-start text-left max-w-2xl px-1">
        <span className="text-[11px] sm:text-xs font-bold tracking-[0.2em] text-[#0284C7] uppercase">
          LETONKU
        </span>
        <h2 className="font-display font-black text-xl sm:text-3xl text-[#172033] tracking-tight leading-snug mt-2">
          “Kopi enak, tempat bertemu,<br className="hidden sm:inline" /> cerita yang terus tumbuh.”
        </h2>
        <p className="text-xs sm:text-base text-[#64748B] leading-relaxed mt-2 sm:mt-3 font-normal">
          Dari secangkir kopi sampai obrolan yang panjang, LetonKu dibuat untuk menjadi bagian dari cerita kamu.
        </p>
      </div>
    </motion.div>
  );
};
